import { useEffect, useState } from 'react';
import {
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  Clock3,
  Flame,
  Gift,
  History,
  RotateCcw,
} from 'lucide-react';
import { Modal } from './Modal';
import './points.css';
import {
  dailyRewardForDay,
  DAY,
  formatPoints,
  SHARE_REWARD,
  streakLength,
  WELCOME_REWARD,
  type Reward,
} from './points-model';
import type { PointsPreview } from './usePointsPreview';
const number = new Intl.NumberFormat('en-US');

function PointMark({ className = '' }: { className?: string }) {
  return (
    <span className={`rd-point-mark ${className}`} aria-hidden="true">
      P<span>/</span>
    </span>
  );
}

function XMark() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M18.9 2H22l-6.8 7.8L23.2 22h-6.3L12 14.6 5.5 22H2.3l7.3-8.5L.8 2h6.5l4.4 6.7L18.9 2Zm-1.1 18h1.7L6.4 3.9H4.6L17.8 20Z" />
    </svg>
  );
}

export function PointsPage({
  preview,
  goToArena,
  environment = 'demo',
}: {
  preview: PointsPreview;
  goToArena: () => void;
  environment?: 'live' | 'demo';
}) {
  const { state, dispatch, balanceCents, committedCents, formattedBalance } =
    preview;
  const [now, setNow] = useState(Date.now());
  const [modal, setModal] = useState<'share' | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const demoNow = now + state.dayOffset * DAY;
  const today = Math.floor(demoNow / DAY);
  const claimed = state.connected && state.claims.includes(today);
  const streak = state.connected ? streakLength(state.claims, today) : 0;
  const welcomeClaimed = state.connected && state.welcome;
  const shareCompleted = state.connected && state.shared;
  const completedDays = claimed ? ((streak - 1) % 7) + 1 : streak % 7;
  const firstDay = streak - completedDays + 1;
  const reward = dailyRewardForDay(claimed ? streak : streak + 1);
  const seconds = Math.max(0, Math.floor(((today + 1) * DAY - demoNow) / 1000));
  const countdown = [
    Math.floor(seconds / 3600),
    Math.floor(seconds / 60) % 60,
    seconds % 60,
  ]
    .map((value) => String(value).padStart(2, '0'))
    .join(':');
  const claim = (reward: Reward) => {
    if (!state.connected) return;
    dispatch({ type: 'claim', reward, day: today });
  };
  const visibleEntries = state.connected ? state.entries : [];
  const entries = historyOpen ? visibleEntries : visibleEntries.slice(0, 3);

  return (
    <section className="rd-points" aria-labelledby="points-title">
      <div className="rd-points-heading">
        <div>
          <h1 id="points-title">Points</h1>
          <p>A little every day. More ways to play.</p>
        </div>
        <details className="rd-points-preview">
          <summary>
            <span className="rd-preview-dot" />
            Design preview
            <ChevronDown size={14} />
          </summary>
          <div className="rd-preview-popover">
            <strong>Try the different states</strong>
            <p>
              {environment === 'live'
                ? 'Demo points only. This preview does not fund live predictions. Loading a scenario or resetting changes only this points preview.'
                : 'Demo points only. Loading a scenario or resetting starts a fresh Arena and points session.'}{' '}
              Changes last until you reload.
            </p>
            <label>
              Load a scenario
              <select
                value=""
                onChange={(event) =>
                  dispatch({
                    type: 'preset',
                    preset: event.target.value,
                    day: Math.floor(now / DAY),
                  })
                }
              >
                <option value="" disabled>
                  Choose a state…
                </option>
                <option value="new">First visit</option>
                <option value="ready">Rewards ready</option>
                <option value="claimed">Rewards claimed</option>
                <option value="streak">Day 7 · bonus ready</option>
              </select>
            </label>
            <button
              disabled={!state.connected}
              onClick={() => dispatch({ type: 'next-day' })}
            >
              <Clock3 size={15} />
              Next demo day
              <ArrowRight size={15} />
            </button>
            <button onClick={() => dispatch({ type: 'reset' })}>
              <RotateCcw size={15} />
              Reset preview
            </button>
          </div>
        </details>
      </div>

      <div className="rd-points-top-grid">
        <section
          className="rd-points-panel rd-points-balance"
          aria-label="Points balance"
        >
          <div className="rd-points-panel-top">
            <span>Available balance</span>
            <PointMark />
          </div>
          <div
            className="rd-points-total"
            key={state.connected ? balanceCents : 'disconnected'}
          >
            <strong>{state.connected ? formattedBalance : '0'}</strong>
            <span>PTS</span>
          </div>
          <p>
            {!state.connected
              ? 'Your points balance will appear here.'
              : environment === 'live'
                ? 'Preview balance · not used for live predictions.'
                : committedCents > 0
                  ? `${formatPoints(committedCents)} PTS in open predictions.`
                  : 'Ready for your next move.'}
          </p>
          <button className="rd-points-button primary" onClick={goToArena}>
            Go to Arena
            <ArrowUpRight size={18} />
          </button>
        </section>

        <section
          className="rd-points-panel rd-points-daily"
          aria-labelledby="daily-title"
        >
          <div className="rd-points-panel-top">
            <h2 id="daily-title">Daily check-in</h2>
            {state.connected && (
              <span className={`rd-points-streak ${streak ? 'active' : ''}`}>
                <Flame size={16} />
                {streak} day{streak === 1 ? '' : 's'} streak
              </span>
            )}
          </div>
          <p>Show up. Keep your streak. Make it count.</p>
          <ol
            className="rd-points-week"
            aria-label={`Days ${firstDay}–${firstDay + 6} rewards`}
          >
            {Array.from({ length: 7 }, (_, index) => {
              const amount = dailyRewardForDay(firstDay + index);
              const done = index < completedDays;
              const current =
                state.connected && !claimed && index === completedDays;
              return (
                <li
                  key={firstDay + index}
                  className={`${done ? 'complete' : ''} ${current ? 'current' : ''} ${index === 6 ? 'bonus' : ''}`}
                  aria-current={current ? 'step' : undefined}
                >
                  <span>Day {firstDay + index}</span>
                  <div className="rd-points-day-icon">
                    {done ? (
                      <Check size={20} />
                    ) : index === 6 ? (
                      <Gift size={21} />
                    ) : (
                      <PointMark />
                    )}
                  </div>
                  <strong>+{number.format(amount)}</strong>
                  <span className="rd-points-day-state">
                    {done ? 'Claimed' : index === 6 ? 'Bonus' : 'PTS'}
                  </span>
                </li>
              );
            })}
          </ol>
          <div className="rd-points-daily-bottom">
            <div>
              {claimed ? (
                <>
                  <span className="rd-points-small-label">Next check-in</span>
                  <strong className="rd-points-countdown">
                    <Clock3 size={15} />
                    {countdown}
                  </strong>
                </>
              ) : (
                <>
                  <span className="rd-points-small-label">Today’s reward</span>
                  <strong>
                    +{number.format(reward)} <span>PTS</span>
                  </strong>
                </>
              )}
            </div>
            <button
              className={`rd-points-button ${claimed ? 'claimed' : 'primary'}`}
              disabled={!state.connected || claimed}
              onClick={() => claim('daily')}
            >
              {claimed ? (
                <>
                  <Check size={17} />
                  Claimed today
                </>
              ) : (
                <>
                  Claim points
                  <ArrowRight size={17} />
                </>
              )}
            </button>
          </div>
          <p className="rd-points-reset-note">
            Daily reset at 00:00 UTC · Check in each day to keep your streak.
          </p>
        </section>
      </div>

      <div className="rd-points-bottom-grid">
        <section className="rd-points-tasks" aria-labelledby="tasks-title">
          <div className="rd-points-section-heading">
            <h2 id="tasks-title">A few ways to earn</h2>
            <span>
              {state.connected
                ? `${Number(welcomeClaimed) + Number(shareCompleted)} / 2 completed`
                : '0 / 2 completed'}
            </span>
          </div>
          <div className={`rd-points-task ${welcomeClaimed ? 'done' : ''}`}>
            <div className="rd-points-task-icon">
              <Gift size={23} />
            </div>
            <div className="rd-points-task-copy">
              <h3>Your first move</h3>
              <p>A welcome reward to get you started.</p>
              <span>
                +{number.format(WELCOME_REWARD)} PTS
                <span className="rd-points-task-dot">·</span>One time
              </span>
            </div>
            <button
              className={`rd-points-button ${welcomeClaimed ? 'claimed' : 'primary'}`}
              disabled={!state.connected || welcomeClaimed}
              onClick={() => claim('welcome')}
            >
              {welcomeClaimed ? (
                <>
                  <Check size={16} />
                  Claimed
                </>
              ) : (
                <>
                  Claim
                  <ArrowUpRight size={16} />
                </>
              )}
            </button>
          </div>
          <div className={`rd-points-task ${shareCompleted ? 'done' : ''}`}>
            <div className="rd-points-task-icon">
              <XMark />
            </div>
            <div className="rd-points-task-copy">
              <h3>Spread the word</h3>
              <p>Share Run or Die with your people on X.</p>
              <span>
                +{SHARE_REWARD} PTS<span className="rd-points-task-dot">·</span>
                One time
              </span>
            </div>
            <button
              className={`rd-points-button ${shareCompleted ? 'claimed' : 'primary'}`}
              disabled={!state.connected || shareCompleted}
              onClick={() => setModal('share')}
            >
              {shareCompleted ? (
                <>
                  <Check size={16} />
                  Completed
                </>
              ) : (
                <>
                  Start
                  <ArrowUpRight size={16} />
                </>
              )}
            </button>
          </div>
          <div className="rd-points-task-footnote">
            <span className="rd-points-little-plus">+</span>More ways to earn,
            coming as we grow.
          </div>
        </section>

        <section className="rd-points-history" aria-labelledby="history-title">
          <div className="rd-points-section-heading">
            <h2 id="history-title">Activity</h2>
            <History size={17} />
          </div>
          {entries.length ? (
            <>
              <ul>
                {entries.map((entry) => (
                  <li key={entry.id}>
                    <span className="rd-points-history-icon">
                      {entry.amountCents < 0 ? (
                        <ArrowUpRight size={16} />
                      ) : entry.amountCents === 0 ? (
                        <History size={16} />
                      ) : (
                        <ArrowDownLeft size={16} />
                      )}
                    </span>
                    <div>
                      <strong>{entry.title}</strong>
                      <span>
                        {entry.day === today
                          ? 'Today'
                          : entry.day === today - 1
                            ? 'Yesterday'
                            : `${today - entry.day} days ago`}{' '}
                        · {entry.status}
                      </span>
                      {entry.detail && <span>{entry.detail}</span>}
                    </div>
                    <strong>
                      {entry.amountCents > 0
                        ? '+'
                        : entry.amountCents < 0
                          ? '−'
                          : ''}
                      {formatPoints(Math.abs(entry.amountCents))}
                      <span>PTS</span>
                    </strong>
                  </li>
                ))}
              </ul>
              {visibleEntries.length > 3 && (
                <button
                  className="rd-points-history-more"
                  onClick={() => setHistoryOpen(!historyOpen)}
                >
                  {historyOpen
                    ? 'Show less'
                    : `View all ${visibleEntries.length} entries`}
                  <ChevronDown size={15} />
                </button>
              )}
            </>
          ) : (
            <div className="rd-points-history-empty">
              <span>
                <History size={23} />
              </span>
              <strong>
                {state.connected ? 'A fresh start.' : 'Your activity'}
              </strong>
              <p>
                {state.connected ? (
                  <>
                    Your rewards and predictions will
                    <br />
                    show up here.
                  </>
                ) : (
                  'Your rewards and predictions will appear here.'
                )}
              </p>
            </div>
          )}
        </section>
      </div>
      <footer className="rd-points-footnote">
        <span>
          Run or Die<span className="rd-points-task-dot">/</span>Points
        </span>
        <span>Demo points · Example reward amounts</span>
        <a href="/">Back to live arena ↗</a>
      </footer>
      <p className="rd-points-sr-only" role="status" aria-live="polite">
        {state.connected ? state.message : ''}
      </p>

      {modal === 'share' && (
        <Modal label="Preview sharing task" close={() => setModal(null)}>
          <div className="rd-points-modal">
            <span className="rd-points-modal-icon">
              <XMark />
            </span>
            <span className="rd-points-modal-eyebrow">DESIGN PREVIEW</span>
            <h2>Good calls deserve company.</h2>
            <p>
              This previews the sharing reward. Nothing is posted to X, and no
              account is connected.
            </p>
            <div className="rd-points-share-preview">
              <span>POST PREVIEW</span>
              <p>
                Run or Die. A new token. One call.
                <br />
                Think you can read the next move?
              </p>
              <span>runordie.xyz</span>
            </div>
            <button
              className="rd-points-button primary"
              onClick={() => {
                claim('share');
                setModal(null);
              }}
            >
              Simulate completed task
              <ArrowRight size={18} />
            </button>
            <span className="rd-points-modal-note">
              Example reward: +{SHARE_REWARD} PTS
            </span>
          </div>
        </Modal>
      )}
    </section>
  );
}
